import React, { useEffect, useState } from 'react';
import './settings.scss';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { getSettingsData } from '../reducer';
import { checkBoxFormatter, regNumberFormatter } from '../../../utils/formatters';
import FileInput from '../../../components/fields/FileInput/FileInput';
import Grid from '../../../components/base/Grid/Grid';
import { showGlobalConfirm } from '../../System/actions';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../components/base/Dialog/Dialog';
import { getSystemData } from '../../System/reducer';
import {
  addLPNToWanted, createRole, deleteFromWanted, deleteRole, importLPNFile, loadWantedList,
} from '../actions';
import { getResponseError, responseLogWithNotification, useUpdatePeriod } from '../../../utils/utils';
import Loader from '../../../components/base/Loader/Loader';
import notificationDecorator, {
  errorNotification,
  successNotification,
} from '../../Notifications/notificationDecorator';
import UploadZone from '../../../components/fields/FileInput/UploadZone';

export default function WantedColumn() {
  const [selected, setSelected] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [newLPN, setNewLPN] = useState('');
  const dispatch = useDispatch();
  const { updatePeriod } = useSelector(getSystemData);
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    loadingWantedList, wantedList, loadingAddToWanted, loadingDeleteFromWanted, loadingImportLPNFile,
  } = useSelector(getSettingsData);
  const loadData = () => {
    const p1 = dispatch(loadWantedList()).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);

  const setFileClear = f => { window.clearFileFunction = f; };

  const cancel = () => {
    if (window.clearFileFunction) {
      window.clearFileFunction();
    }
  };

  const handleImportLPNFile = data => {
    dispatch(importLPNFile(data)).then(() => {
      loadData();
      setShowUpload(false);
      successNotification(formatMessage({ id: 'wanted.import' }), formatMessage({ id: 'wanted.import.success' }));
      cancel();
    }).catch(response => {
      if (response && response.errors && response.errors.length) {
        return errorNotification(formatMessage({ id: 'wanted.import' }), response.errors.map(i => i.message).slice(0, 3).join('\n '));
      }
      errorNotification(formatMessage({ id: 'wanted.import' }), getResponseError(response));
      cancel();
    });
  };

  const handleDelete = event => {
    dispatch(showGlobalConfirm({
      confirmMessage: formatMessage({ id: 'exception.delete' }),
      confirmButtonText: formatMessage({ id: 'app.delete' }),
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteFromWanted(selected)).then(() => {
        loadData();
        successNotification(formatMessage({ id: 'exception.delete.title' }), formatMessage({ id: 'exception.delete.success' }));
      }).catch(response => {
        errorNotification(formatMessage({ id: 'exception.delete.title' }), getResponseError(response));
      });
    }).catch(() => {});

    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const handleAddLPN = event => {
    dispatch(addLPNToWanted(newLPN)).then(() => {
      loadData().then(() => {
        setShowCreate(false);
        setNewLPN('');
      });
      successNotification(formatMessage({ id: 'wanted.add' }), formatMessage({ id: 'wanted.add.success' }));
    }).catch(response => errorNotification(formatMessage({ id: 'wanted.add' }), getResponseError(response)));
  };

  const headerCheckBoxFormatter = (id, item) => {
    const isAll = wantedList.every(p => selected.includes(p.id));
    const isSome = wantedList.some(p => selected.includes(p.id)) && !isAll;
    const onChange = () => {
      if (isAll) {
        setSelected([]);
      } else {
        setSelected(wantedList.map(p => p.id));
      }
    };
    return checkBoxFormatter(isAll, onChange, classNames({ 'some-child': isSome }, { 'every-child': isAll }));
  };

  const rowCheckBoxFormatter = (_, item) => {
    const onChange = () => {
      const index = selected.indexOf(item.id);
      if (index >= 0) {
        const newSelected = [...selected];
        newSelected.splice(index, 1);
        setSelected(newSelected);
      } else {
        setSelected([...selected, item.id]);
      }
    };
    return checkBoxFormatter(selected.includes(item.id), onChange);
  };

  const columns = [
    {
      dataField: 'checkbox',
      text: formatMessage({ id: 'select.row' }),
      disableSort: true,
      headerFormatter: headerCheckBoxFormatter,
      formatter: rowCheckBoxFormatter,
      dataClassName: 'checkbox-column',
      headerClassName: 'checkbox-column',
    },
    {
      dataField: 'lp_text',
      text: formatMessage({ id: 'reg_number' }),
      formatter: regNumberFormatter,
      headerClassName: 'reg-number-column',
      disableSort: true,
    },
  ];

  const renderContent = () => (
    <div
      className="wanted-data"
    >
      <Grid columns={columns} data={wantedList} />
    </div>
  );

  const renderCreateDialog = () => (
    <Dialog
      className={classNames('calibration-dialog-container')}
      onClose={() => { setShowCreate(false); }}
      open
    >
      <DialogTitle header={formatMessage({ id: 'wanted.add' })} title={<Icons onClick={() => { setShowCreate(false); }} customIcon icon="close" className="close" />} />
      <DialogContent className="hff-content">
        <TextInputWithLabel
          label={formatMessage({ id: 'reg_number' })}
          variant="material"
          placeholder={formatMessage({ id: 'reg_number.placeholder.long' })}
          inputProps={{
            value: newLPN,
            onChange: e => setNewLPN(e.target.value),
          }}
        />
      </DialogContent>
      <DialogFooter>
        <Button onClick={handleAddLPN}>{formatMessage({ id: 'app.save' })}</Button>
        <Button variant="secondary" onClick={() => { setShowCreate(false); }}>{formatMessage({ id: 'app.close' })}</Button>
      </DialogFooter>
    </Dialog>
  );

  const renderUpload = () => (
    <UploadZone inputProps={{ onChange: handleImportLPNFile }} setClear={setFileClear} zoneText={formatMessage({ id: 'wanted.import.file' })} />
  );

  return (
    <div className="settings-options frame wanted-column">
      <Loader loading={loadingWantedList || loadingAddToWanted || loadingDeleteFromWanted || loadingImportLPNFile} />
      <div className="settings-edit">
        <div className="settings-header">
          <div className="settings-label">{formatMessage({ id: 'wanted.numbers' })}</div>
          <div className="settings-buttons wanted-buttons">
            {wantedList.length ? (
              <>
                <div className="settings-icon-btn" onClick={() => { setShowUpload(!showUpload); }}>
                  <Icons customIcon icon="file-export" className="icon" />
                </div>
                <div className="control-delimiter" />
              </>
            ) : null}
            <div className="settings-icon-btn" onClick={() => { setShowCreate(true); }}>
              <Icons width={26} height={26} customIcon icon="plus" className="icon" />
            </div>

            {wantedList.length ? (
              <>
                <div className="control-delimiter" />
                <div className={classNames('settings-icon-btn blue', { disabled: !selected.length })} onClick={handleDelete}>
                  <Icons width={20} height={20} customIcon icon="trash" className="icon" />
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="settings-form">
          <div className="settings-view-form">
            {!wantedList.length || showUpload ? renderUpload() : renderContent()}
            {showCreate ? renderCreateDialog() : null}
          </div>
        </div>
      </div>
    </div>
  );
}
